import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bot, Check, Pencil, Play, Trash2, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import Spinner from '@/components/ui/Spinner';
import StatusPill from '@/components/ui/StatusPill';
import type { Project } from '@/hooks/useProject';
import type { Readiness } from '@/lib/projectReadiness';
import { productionTypeMeta } from '@/lib/labels';

/**
 * Identity + the one action you came here to take.
 *
 * "New Production" sits in the header rather than inside a panel because it is the
 * page's primary action; everything else on the page exists to prepare for it.
 * Rename and Delete are owner housekeeping, so they stay quiet until hovered.
 */
export default function ProjectHeader({
  project,
  readiness,
  onNewProduction,
  onRename,
  onDelete,
}: {
  project: Project;
  /** Whether a production can start, and if not, precisely what is missing. */
  readiness: Readiness;
  onNewProduction: () => void;
  /** Resolves true once the new title is persisted. */
  onRename: (title: string) => Promise<boolean>;
  /** Resolves true once the project is gone; the caller navigates away. */
  onDelete: () => Promise<boolean>;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation(['dashboard', 'common']);
  const type = productionTypeMeta(t, project.production_type);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const startEditing = () => {
    setDraft(project.title || '');
    setEditing(true);
  };

  const commitRename = async () => {
    setSaving(true);
    try {
      // A no-op or failed rename still closes the editor; the hook flashes the reason.
      await onRename(draft);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  // ConfirmDialog closes itself on confirm; we only own the in-flight state.
  const confirmDeleteProject = async () => {
    setDeleting(true);
    if (await onDelete()) navigate('/projects');
    else setDeleting(false);
  };

  return (
    <div>
      <button
        onClick={() => navigate('/projects')}
        className="mb-4 inline-flex items-center gap-1.5 text-[13px] text-faint transition-colors hover:text-primary"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        {t('project.allProjects')}
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          {editing ? (
            <div className="flex items-center gap-2">
              <input
                autoFocus
                value={draft}
                disabled={saving}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitRename();
                  if (e.key === 'Escape') setEditing(false);
                }}
                aria-label={t('project.rename')}
                className="w-full max-w-lg rounded-md border border-border bg-background px-2 py-0.5
                           font-display text-[22px] font-semibold outline-none focus:border-primary"
              />
              <Button variant="ghost" size="sm" className="h-8 px-2" disabled={saving} onClick={commitRename}>
                {saving ? <Spinner /> : <Check className="h-4 w-4" />}
                <span className="sr-only">{t('action.save', { ns: 'common' })}</span>
              </Button>
              <Button variant="ghost" size="sm" className="h-8 px-2" disabled={saving} onClick={() => setEditing(false)}>
                <X className="h-4 w-4" />
                <span className="sr-only">{t('action.cancel', { ns: 'common' })}</span>
              </Button>
            </div>
          ) : (
            // Owner housekeeping stays quiet until you look for it, and deliberately
            // sits here rather than beside the primary CTA — a destructive action
            // should never be one slip away from "New Production".
            <div className="group flex items-center gap-1">
              <h2 className="m-0 truncate font-display text-[22px] font-semibold">{project.title}</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={startEditing}
                title={t('project.rename')}
                aria-label={t('project.rename')}
                className="ml-1 h-7 px-2 text-faint opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={deleting}
                onClick={() => setConfirmDelete(true)}
                title={t('project.delete')}
                aria-label={t('project.delete')}
                className="h-7 px-2 text-faint opacity-0 transition-opacity hover:text-destructive
                           focus-visible:opacity-100 group-hover:opacity-100"
              >
                {deleting ? <Spinner /> : <Trash2 className="h-3.5 w-3.5" />}
              </Button>
            </div>
          )}

          <div className="mt-2 flex flex-wrap items-center gap-2.5">
            <StatusPill status={project.status}>{project.status}</StatusPill>
            <StatusPill tone="neutral">{type.label}</StatusPill>
            {project.provenance?.source_system && (
              <Badge
                variant="outline"
                className="gap-1 text-muted-foreground"
                title={
                  project.provenance.on_behalf_of ? t('project.onBehalfOf', { who: project.provenance.on_behalf_of }) : undefined
                }
              >
                <Bot className="h-3 w-3" />
                {t('project.delegated', { system: project.provenance.source_system })}
              </Badge>
            )}
            <span className="text-xs text-faint">
              {t('project.created', { date: new Date(project.created_at).toLocaleDateString() })}
            </span>
            <span className="text-xs text-faint">{t('project.productionCount', { count: project.production_count })}</span>
          </div>
        </div>

        {/* Short reason only. The setup checklist directly below spells out the
            full sequence, so repeating it here would just be noise. */}
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <Button disabled={!readiness.ready} onClick={onNewProduction}>
            <Play className="mr-2 h-4 w-4" />
            {t('launch.start')}
          </Button>
          {!readiness.ready && (
            <p className="m-0 text-right text-[11.5px] text-faint">{t(`readiness.short.${readiness.reason}`)}</p>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t('project.deleteTitle', { title: project.title })}
        description={t('project.deleteDescription')}
        warning={t('project.deleteWarning', { count: project.production_count })}
        confirmLabel={t('project.delete')}
        cancelLabel={t('action.cancel', { ns: 'common' })}
        destructive
        onConfirm={confirmDeleteProject}
      />
    </div>
  );
}
